import { BACK_BUTTON_NAME, ORDER_BAR_ID } from "@/constants/layout";

/**
 * The React Native bridge and the back-button plumbing, recovered from the
 * original's module 34734.
 *
 * This app is served both as a website and inside the Zenvix RN shell, and
 * the two behave differently in three places: opening an external link, closing
 * the top overlay, and (elsewhere) registering the customer. Everything here is
 * a plain function rather than a hook because the original calls it from event
 * handlers, not from render.
 */

/** `s` — true only inside the RN web view. */
export const isWebView = (): boolean =>
  typeof window !== "undefined" && window.ReactNativeWebView != null;

/** `c` — post a typed message to the shell. Returns false on plain web. */
export const postToWebView = (type: string, payload?: unknown): boolean => {
  const bridge = typeof window === "undefined" ? null : window.ReactNativeWebView;
  if (!bridge) return false;
  let message: string;
  try {
    message = JSON.stringify({ type, payload });
  } catch {
    return false;
  }
  bridge.postMessage(message);
  return true;
};

export const WEBVIEW_OPEN_LINK = "WEBVIEW_OPEN_LINK";

/**
 * The native shell's hardware-back notification. The shell posts it INTO the
 * page; nothing in the page posts it out.
 */
export const WEBVIEW_RENDER_BACK_PRESS = "WEBVIEW_RENDER_BACK_PRESS";

/**
 * Posted once on mount, `{ isLoading: false }`, to tell the shell the web view
 * has rendered and its splash can go.
 *
 * The shipped shell has no handler for it — its APK carries 24 `WEBVIEW_*`
 * strings and none of the `OO_*` ones — so this is a no-op in practice. It is
 * recovered anyway because the original sends it from exactly here, and a
 * later shell build may well listen.
 */
export const WEBVIEW_OO_SET_LOADING = "WEBVIEW_OO_SET_LOADING";
export const WEBVIEW_LAN_OFFLINE_ORDER = "WEBVIEW_LAN_OFFLINE_ORDER";

export const relayOfflineOrderToNative = (order: Record<string, unknown>): boolean =>
  postToWebView(WEBVIEW_LAN_OFFLINE_ORDER, { order });

/**
 * `Tm.o` — the original's own `safeJsonParse`, module `9387`:
 *
 * ```js
 * function i(e) { try { return JSON.parse(e); } catch (e) { return null; } }
 * ```
 *
 * A `message` event can carry anything at all (browser extensions and dev
 * tooling post to `window` constantly), so a parse failure has to be `null`
 * rather than a thrown error that kills the listener.
 */
export const parseBridgeMessage = (data: unknown): { type?: string } | null => {
  if (typeof data !== "string") return null;
  try {
    const parsed: unknown = JSON.parse(data);
    return typeof parsed === "object" && parsed !== null
      ? (parsed as { type?: string })
      : null;
  } catch {
    return null;
  }
};

/**
 * `d` (`vz`) — the ONLY way the original opens an external URL, including
 * `tel:` links. It is deliberately not an `<a href>`: inside the shell the URL
 * has to travel over the bridge so the native side can open it.
 */
export const openLink = (url: string): void => {
  if (isWebView()) {
    postToWebView(WEBVIEW_OPEN_LINK, url);
    return;
  }
  window.open(url, "_blank", "noopener,noreferrer");
};

let lastBackClickAt = 0;

/**
 * `u` (`yZ`) — click the LAST rendered back/close control, which is the topmost
 * overlay's. The order bar carries the same name, so it is skipped whenever any
 * other back control exists. `debounce` is used by the `popstate` path, where a
 * held-down back button would otherwise unwind several screens at once.
 */
export const clickBackButton = (debounce = false): void => {
  if (typeof document === "undefined") return;
  const buttons = document.getElementsByName(BACK_BUTTON_NAME);
  let target = buttons[buttons.length - 1] as HTMLElement | undefined;
  if (target != null && target.id === ORDER_BAR_ID && buttons.length > 1) {
    target = buttons[buttons.length - 2] as HTMLElement | undefined;
  }
  if (target == null || typeof target.click !== "function") return;
  if (!debounce) {
    target.click();
    return;
  }
  const now = Date.now();
  if (now - lastBackClickAt <= 300) return;
  lastBackClickAt = now;
  target.click();
};
